import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { cn } from "@/lib/utils";
import { LegalLinks } from "@/components/LegalLinks";
/**
 * Jahresrückblick — one person's year in the chat, as story cards in the
 * CRISIO look. Public route: viewers log in with Twitch (same login as the
 * casino); Discord-only accounts arrive with ?t=<token> from a DM.
 */
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const num = (n) => n.toLocaleString("de-DE");
const shortDay = (key) => `${key.slice(8, 10)}.${key.slice(5, 7)}.`;
const longDate = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "long", timeZone: "Europe/Berlin" });
/** Twitch names have no spaces to wrap at, so long ones get a smaller size instead. */
const nameSize = (name) => name.length > 20 ? "text-2xl" : name.length > 16 ? "text-3xl" : name.length > 12 ? "text-4xl" : "text-5xl";
const TONES = {
    ink: "bg-ink text-creme",
    creme: "bg-creme text-ink",
    magenta: "bg-brand-dark text-white",
};
function CountUp({ to, ms = 1200 }) {
    const [v, setV] = useState(0);
    useEffect(() => {
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce)
            return setV(to);
        let raf = 0;
        const start = performance.now();
        const tick = (t) => {
            const p = Math.min(1, (t - start) / ms);
            setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
            if (p < 1)
                raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [to, ms]);
    return _jsx(_Fragment, { children: num(v) });
}
function Kicker({ children }) {
    return _jsx("p", { className: "font-mono text-xs uppercase tracking-[0.25em] opacity-70", children: children });
}
function Big({ children, className }) {
    return _jsx("p", { className: cn("font-display uppercase leading-[0.9] break-words hyphens-auto", className), children: children });
}
function Sticker({ children, className }) {
    return (_jsx("span", { className: cn("inline-block -rotate-3 rounded-lg bg-brand px-3 py-1 font-display text-lg uppercase text-ink", className), children: children }));
}
function List({ items }) {
    return (_jsx("ol", { className: "space-y-3", children: items.map((it, i) => (_jsxs("li", { className: "flex items-baseline gap-4", children: [_jsx("span", { className: "font-display text-4xl text-brand", children: i + 1 }), _jsx("span", { className: "min-w-0 flex-1 truncate font-display text-3xl uppercase", children: it.label }), _jsx("span", { className: "shrink-0 font-mono text-sm opacity-70", children: it.value })] }, it.label))) }));
}
/** The cards for one person. A card without data for them is left out, not shown empty. */
function buildCards(s) {
    const cards = [];
    const name = s.displayName;
    cards.push({
        key: "intro",
        tone: "ink",
        body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "CriStream \u00B7 Jahresr\u00FCckblick" }), _jsx(Big, { className: "mt-6 text-[7rem] text-brand", children: s.year }), _jsx(Big, { className: "mt-4 text-5xl", children: "Dein Jahr im Chat," }), _jsx(Big, { className: cn("mt-1 text-brand", nameSize(name)), children: name }), _jsx("p", { className: "mt-8 text-lg opacity-80", children: "Tipp rechts f\u00FCr weiter, links f\u00FCr zur\u00FCck." })] })),
    });
    if (s.messages.total > 0) {
        const both = s.messages.twitch > 0 && s.messages.discord > 0;
        cards.push({
            key: "messages",
            tone: "magenta",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Du hast geschrieben" }), _jsx(Big, { className: "mt-6 text-[6.5rem]", children: _jsx(CountUp, { to: s.messages.total }) }), _jsx(Big, { className: "text-5xl", children: "Nachrichten" }), both && (_jsxs("p", { className: "mt-6 font-mono text-sm", children: [num(s.messages.twitch), " auf Twitch \u00B7 ", num(s.messages.discord), " im Discord"] })), s.rank && (_jsxs("p", { className: "mt-8 text-2xl", children: ["Das ist ", _jsxs(Sticker, { className: "bg-creme", children: ["Platz ", s.rank.place] }), " von ", num(s.rank.of), "."] }))] })),
        });
    }
    if (s.messages.activeDays > 0) {
        cards.push({
            key: "days",
            tone: "creme",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Dabei an" }), _jsx(Big, { className: "mt-6 text-[6.5rem] text-brand-dark", children: _jsx(CountUp, { to: s.messages.activeDays }) }), _jsx(Big, { className: "text-5xl", children: "Tagen" }), s.longestStreak && s.longestStreak.days > 1 && (_jsxs("p", { className: "mt-8 text-2xl leading-snug", children: ["L\u00E4ngste Serie: ", _jsxs("strong", { children: [s.longestStreak.days, " Tage am St\u00FCck"] }), ", vom ", shortDay(s.longestStreak.from), " bis", " ", shortDay(s.longestStreak.to)] }))] })),
        });
    }
    if (s.busiestHour && s.busiestWeekday) {
        cards.push({
            key: "time",
            tone: "ink",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Deine Zeit" }), _jsxs(Big, { className: "mt-6 text-[6rem] text-brand", children: [s.busiestHour.hour, " Uhr"] }), _jsxs("p", { className: "mt-4 text-2xl leading-snug", children: ["Da warst du am lautesten. Lieblingstag: ", _jsx("strong", { children: WEEKDAYS[s.busiestWeekday.weekday] }), s.busiestMonth && (_jsxs(_Fragment, { children: [", st\u00E4rkster Monat: ", _jsx("strong", { children: MONTHS[s.busiestMonth.month - 1] })] })), "."] }), s.nightShare >= 0.15 && (_jsxs("p", { className: "mt-6 font-mono text-sm opacity-80", children: [Math.round(s.nightShare * 100), " % nach Mitternacht."] }))] })),
        });
    }
    if (s.firstMessage) {
        cards.push({
            key: "first",
            tone: "creme",
            body: (_jsxs(_Fragment, { children: [_jsxs(Kicker, { children: ["Deine erste Nachricht ", s.year] }), _jsxs("blockquote", { className: "mt-8 border-l-8 border-brand pl-5 text-3xl font-semibold leading-snug break-words", children: ["\u201E", s.firstMessage.text, "\u201C"] }), _jsxs("p", { className: "mt-6 font-mono text-sm opacity-70", children: [longDate(s.firstMessage.at), " \u00B7 ", s.firstMessage.platform === "discord" ? "Discord" : "Twitch"] })] })),
        });
    }
    if (s.topSounds.length > 0) {
        cards.push({
            key: "sounds",
            tone: "magenta",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Dein Soundtrack" }), _jsx(Big, { className: "mb-8 mt-4 text-5xl", children: "Deine Sounds" }), _jsx(List, { items: s.topSounds.map((x) => ({ label: x.name, value: `${x.count}×` })) })] })),
        });
    }
    if (s.topCommands.length > 0) {
        cards.push({
            key: "commands",
            tone: "ink",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Am meisten getippt" }), _jsx(Big, { className: "mb-8 mt-4 text-5xl", children: "Deine Befehle" }), _jsx(List, { items: s.topCommands.map((x) => ({ label: `!${x.trigger}`, value: `${x.count}×` })) })] })),
        });
    }
    if (s.hugs.given > 0 || s.hugs.received > 0) {
        cards.push({
            key: "hugs",
            tone: "creme",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Umarmungen" }), _jsxs("div", { className: "mt-6 grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx(Big, { className: "text-7xl text-brand-dark", children: num(s.hugs.given) }), _jsx("p", { className: "text-lg", children: "verteilt" })] }), _jsxs("div", { children: [_jsx(Big, { className: "text-7xl text-brand-dark", children: num(s.hugs.received) }), _jsx("p", { className: "text-lg", children: "bekommen" })] })] }), s.hugs.favorite && (_jsxs("p", { className: "mt-10 text-2xl leading-snug", children: ["Am h\u00E4ufigsten umarmt: ", _jsx(Sticker, { children: s.hugs.favorite.name }), " ", _jsxs("span", { className: "font-mono text-sm", children: ["(", s.hugs.favorite.count, "\u00D7)"] })] }))] })),
        });
    }
    const extras = [];
    if (s.events.followedAt)
        extras.push(`Gefolgt seit ${longDate(s.events.followedAt)}`);
    if (s.events.subs > 0)
        extras.push(`${s.events.subs}× abonniert`);
    if (s.events.giftedSubs > 0)
        extras.push(`${num(s.events.giftedSubs)} Subs verschenkt`);
    if (s.events.bits > 0)
        extras.push(`${num(s.events.bits)} Bits`);
    if (s.events.raids > 0)
        extras.push(`${s.events.raids}× geraidet`);
    if (s.quotesAdded > 0)
        extras.push(`${s.quotesAdded} ${s.quotesAdded === 1 ? "Zitat" : "Zitate"} gemerkt`);
    if (s.wishes.submitted > 0)
        extras.push(`${s.wishes.submitted} ${s.wishes.submitted === 1 ? "Wunsch" : "Wünsche"} eingereicht${s.wishes.done ? `, ${s.wishes.done} erfüllt` : ""}`);
    if (s.watchMinutes && s.watchMinutes >= 60)
        extras.push(`${num(Math.round(s.watchMinutes / 60))} Stunden zugeschaut (insgesamt)`);
    if (extras.length > 0) {
        cards.push({
            key: "extras",
            tone: "ink",
            body: (_jsxs(_Fragment, { children: [_jsx(Kicker, { children: "Und au\u00DFerdem" }), _jsx("ul", { className: "mt-8 space-y-4", children: extras.map((e) => (_jsxs("li", { className: "flex gap-3 text-2xl leading-snug", children: [_jsx("span", { className: "text-brand", children: "\u2726" }), _jsx("span", { children: e })] }, e))) })] })),
        });
    }
    cards.push({
        key: "persona",
        tone: "magenta",
        body: (_jsxs(_Fragment, { children: [_jsxs(Kicker, { children: ["Dein ", s.year, " in einem Wort"] }), _jsx(Big, { className: "mt-8 text-7xl", children: "Du bist" }), _jsx(Big, { className: cn("mt-2 text-ink", s.persona.title.length > 11 ? "text-6xl" : "text-[5.5rem]"), children: s.persona.title }), _jsx("p", { className: "mt-8 text-2xl leading-snug", children: s.persona.line })] })),
    });
    cards.push({
        key: "summary",
        tone: "creme",
        body: (_jsxs("div", { className: "rounded-3xl border-4 border-ink bg-white/60 p-6", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "flex h-10 w-10 -rotate-6 items-center justify-center rounded-xl bg-brand-dark font-display text-white", children: "CS" }), _jsxs("span", { className: "font-mono text-xs uppercase tracking-[0.2em]", children: ["R\u00FCckblick ", s.year] })] }), _jsx(Big, { className: cn("mt-5", nameSize(name)), children: name }), _jsx(Sticker, { className: "mt-3", children: s.persona.title }), _jsxs("dl", { className: "mt-6 grid grid-cols-2 gap-x-4 gap-y-4", children: [_jsxs("div", { children: [_jsx("dt", { className: "font-mono text-[11px] uppercase opacity-60", children: "Nachrichten" }), _jsx("dd", { className: "font-display text-3xl", children: num(s.messages.total) })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-mono text-[11px] uppercase opacity-60", children: "Tage dabei" }), _jsx("dd", { className: "font-display text-3xl", children: num(s.messages.activeDays) })] }), s.busiestHour && (_jsxs("div", { children: [_jsx("dt", { className: "font-mono text-[11px] uppercase opacity-60", children: "Deine Uhrzeit" }), _jsxs("dd", { className: "font-display text-3xl", children: [s.busiestHour.hour, " Uhr"] })] })), s.topSounds[0] ? (_jsxs("div", { className: "min-w-0", children: [_jsx("dt", { className: "font-mono text-[11px] uppercase opacity-60", children: "Top-Sound" }), _jsx("dd", { className: "truncate font-display text-3xl uppercase", children: s.topSounds[0].name })] })) : (s.rank && (_jsxs("div", { children: [_jsx("dt", { className: "font-mono text-[11px] uppercase opacity-60", children: "Platz" }), _jsx("dd", { className: "font-display text-3xl", children: s.rank.place })] })))] }), _jsx("p", { className: "mt-6 text-center font-mono text-[11px] opacity-60", children: "Screenshot machen und teilen \u2726" })] })),
    });
    return cards;
}
function Stories({ stats, onLeave }) {
    const cards = useMemo(() => buildCards(stats), [stats]);
    const [i, setI] = useState(0);
    const next = useCallback(() => setI((v) => Math.min(v + 1, cards.length - 1)), [cards.length]);
    const prev = useCallback(() => setI((v) => Math.max(v - 1, 0)), []);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight" || e.key === " ")
                next();
            if (e.key === "ArrowLeft")
                prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev]);
    const card = cards[i];
    const last = i === cards.length - 1;
    return (_jsxs("div", { className: cn("relative flex h-full flex-col transition-colors duration-500", TONES[card.tone]), children: [_jsx("div", { className: "absolute inset-x-0 top-0 z-10 flex gap-1 p-3", "aria-hidden": "true", children: cards.map((c, n) => (_jsxs("div", { className: "relative h-1 flex-1 overflow-hidden rounded-full", children: [_jsx("div", { className: "absolute inset-0 bg-current opacity-25" }), _jsx("div", { className: cn("relative h-full bg-current transition-all duration-300", n <= i ? "w-full" : "w-0") })] }, c.key))) }), _jsx("button", { className: "absolute inset-y-0 left-0 z-0 w-1/3", onClick: prev, "aria-label": "Zur\u00FCck" }), _jsx("button", { className: "absolute inset-y-0 right-0 z-0 w-2/3", onClick: next, "aria-label": "Weiter", disabled: last }), _jsx("div", { className: "wrapped-in pointer-events-none relative flex flex-1 flex-col justify-center px-7 pb-16 pt-14", children: card.body }, card.key), _jsxs("div", { className: "relative z-10 flex items-center justify-between px-5 pb-5 font-mono text-[11px] opacity-70", children: [_jsxs("span", { children: [i + 1, " / ", cards.length] }), last ? (_jsxs("span", { className: "flex gap-4", children: [_jsx("button", { className: "underline", onClick: () => setI(0), children: "Nochmal" }), onLeave && (_jsx("button", { className: "underline", onClick: onLeave, children: "Abmelden" }))] })) : (_jsx("span", { children: "Tippen \u203A" }))] })] }));
}
function Panel({ children }) {
    return (_jsxs("div", { className: "relative flex h-full flex-col justify-center bg-ink px-7 text-creme", children: [children, _jsx(LegalLinks, { className: "absolute inset-x-0 bottom-5" })] }));
}
function Countdown({ to }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);
    const ms = Math.max(0, new Date(to).getTime() - now);
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor((ms % 86_400_000) / 3_600_000);
    return (_jsx("p", { className: "font-display text-6xl uppercase text-brand", children: days > 0 ? `${days} ${days === 1 ? "Tag" : "Tage"}` : `${hours} ${hours === 1 ? "Stunde" : "Stunden"}` }));
}
export function WrappedPage() {
    const { channelName = "thecrisio" } = useParams();
    usePageTitle("Jahresrückblick");
    const shareToken = new URLSearchParams(window.location.search).get("t");
    const [token, setToken] = useState(() => {
        try {
            return localStorage.getItem("token");
        }
        catch {
            return null;
        }
    });
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // Back from the Twitch login: same handover as the casino.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get("token");
        const r = params.get("refresh");
        if (t && r) {
            try {
                localStorage.setItem("token", t);
                localStorage.setItem("refreshToken", r);
            }
            catch { }
            window.history.replaceState({}, "", window.location.pathname);
            setToken(t);
        }
    }, []);
    const load = useCallback(async () => {
        setError(null);
        const url = shareToken
            ? `/api/wrapped/${encodeURIComponent(channelName)}/t/${encodeURIComponent(shareToken)}`
            : `/api/wrapped/${encodeURIComponent(channelName)}/me`;
        if (!shareToken && !token)
            return;
        try {
            const res = await fetch(url, { headers: token && !shareToken ? { Authorization: `Bearer ${token}` } : {} });
            if (res.status === 401) {
                try {
                    localStorage.removeItem("token");
                }
                catch { }
                setToken(null);
                return;
            }
            const body = await res.json();
            if (!body.success)
                throw new Error(body.error ?? "Fehler");
            setState(body.data);
        }
        catch (e) {
            setError(shareToken ? "Dieser Link funktioniert nicht (mehr)." : "Das hat nicht geklappt. Lad die Seite neu.");
        }
    }, [channelName, shareToken, token]);
    useEffect(() => {
        load();
    }, [load]);
    const call = async (method) => {
        if (!token)
            return;
        setBusy(true);
        try {
            await fetch(`/api/wrapped/${encodeURIComponent(channelName)}/me`, { method, headers: { Authorization: `Bearer ${token}` } });
            await load();
        }
        finally {
            setBusy(false);
        }
    };
    const loginUrl = `/api/auth/twitch/viewer?returnTo=${encodeURIComponent(`/rueckblick/${channelName}`)}`;
    const btn = "mt-8 inline-block rounded-full bg-brand px-8 py-4 text-center font-display text-xl uppercase text-ink transition hover:bg-creme disabled:opacity-50";
    let content;
    if (error) {
        content = (_jsxs(Panel, { children: [_jsx(Big, { className: "text-5xl", children: "Hoppla." }), _jsx("p", { className: "mt-4 text-lg opacity-80", children: error })] }));
    }
    else if (!shareToken && !token) {
        content = (_jsxs(Panel, { children: [_jsx("p", { className: "font-mono text-xs uppercase tracking-[0.25em] opacity-70", children: "CriStream \u00B7 Jahresr\u00FCckblick" }), _jsx(Big, { className: "mt-6 text-6xl", children: "Dein Jahr im Chat" }), _jsx("p", { className: "mt-6 text-lg leading-relaxed opacity-80", children: "Wie viel du geschrieben hast, wann du am lautesten warst, deine Sounds, deine Umarmungen. Nur f\u00FCr dich, und nur wenn du mitmachen willst." }), _jsx("a", { href: loginUrl, className: btn, children: "Mit Twitch einloggen" })] }));
    }
    else if (!state) {
        content = (_jsx(Panel, { children: _jsx("p", { className: "animate-pulse font-display text-4xl uppercase text-brand", children: "Wird gez\u00E4hlt \u2026" }) }));
    }
    else if (!state.joined) {
        content = (_jsxs(Panel, { children: [_jsx(Big, { className: "text-6xl", children: "Mitmachen?" }), _jsxs("p", { className: "mt-6 text-lg leading-relaxed opacity-80", children: ["Am 23.12. bekommst du deinen Jahresr\u00FCckblick ", state.year, ": Nachrichten, Lieblings-Sounds, deine Uhrzeit, deine Serie. Den sieht nur du. Wer nicht mitmacht, taucht auch im Community-R\u00FCckblick nicht namentlich auf."] }), _jsx("button", { className: btn, disabled: busy, onClick: () => call("POST"), children: "Ich bin dabei" }), _jsx("p", { className: "mt-4 font-mono text-xs opacity-60", children: "Geht auch im Chat: !r\u00FCckblick" })] }));
    }
    else if (!state.stats) {
        content = (_jsxs(Panel, { children: [_jsx("p", { className: "font-mono text-xs uppercase tracking-[0.25em] opacity-70", children: "Du bist dabei \u2726 Noch" }), _jsx("div", { className: "mt-4", children: _jsx(Countdown, { to: state.revealAt }) }), _jsxs("p", { className: "mt-4 text-lg leading-relaxed opacity-80", children: ["bis zu deinem Jahresr\u00FCckblick ", state.year, ". Am 23.12. geht's hier los."] }), !shareToken && (_jsx("button", { className: "mt-10 self-start font-mono text-xs underline opacity-60", disabled: busy, onClick: () => call("DELETE"), children: "Doch nicht mitmachen" }))] }));
    }
    else {
        content = (_jsxs(_Fragment, { children: [state.preview && (_jsx("div", { className: "absolute inset-x-0 top-7 z-20 mx-auto w-fit rounded-full bg-creme px-3 py-1 font-mono text-[10px] uppercase text-ink", children: "Vorschau, nur f\u00FCr dich sichtbar" })), _jsx(Stories, { stats: state.stats, onLeave: shareToken ? undefined : () => call("DELETE") })] }));
    }
    return (_jsxs("div", { className: "min-h-dvh bg-[#0b0a0d] font-sans md:py-6", children: [_jsx("style", { children: `
        @keyframes wrapped-in { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: none } }
        .wrapped-in > * { animation: wrapped-in .5s cubic-bezier(.2,.7,.2,1) both }
        .wrapped-in > *:nth-child(2) { animation-delay: .08s } .wrapped-in > *:nth-child(3) { animation-delay: .16s }
        .wrapped-in > *:nth-child(4) { animation-delay: .24s } .wrapped-in > *:nth-child(5) { animation-delay: .32s }
        @media (prefers-reduced-motion: reduce) { .wrapped-in > * { animation: none } }
      ` }), _jsx("main", { lang: "de", className: "relative mx-auto h-dvh max-w-[460px] overflow-hidden md:h-[min(860px,calc(100dvh-3rem))] md:rounded-[2rem] md:shadow-2xl", children: content })] }));
}
